import i18n from '@/helper/i18n';
import dayjs from 'dayjs';

import { getStationInfo, getFlightTerminalInfo } from '@/helper/station';

const t = i18n.chain;
export type flightDetailsParserCallBackType = (
  journeyLegDetails: AACheckout.JourneyLegDetail[],
  journeyLegs: AACheckout.JourneyLeg[],
  stationDetails: AACheckout.StationDetails,
) => Promise<void>;

export class FlightDetailsParser {
  private pageData;
  private protectedByActionCallback;
  private selfTransferActionCallback;
  private terminalInfoArray: FlightTerminalInfo[] = [];
  private terminalInfoFullLoaded = false;
  private flightDetailsParserCallBack: flightDetailsParserCallBackType;

  private journeyLegDetails: AACheckout.JourneyLegDetail[];
  private journeyLegs: AACheckout.JourneyLeg[];
  private stationDetails: AACheckout.StationDetails;

  constructor(
    pageData,
    protectedByActionCallback,
    selfTransferActionCallback,
    flightDetailsParsedCallBack: flightDetailsParserCallBackType,
  ) {
    this.pageData = pageData;
    this.protectedByActionCallback = protectedByActionCallback;
    this.selfTransferActionCallback = selfTransferActionCallback;
    this.flightDetailsParserCallBack = flightDetailsParsedCallBack;
  }

  async convertLeg(journeyLegs, s, i) {
    const protectedByActionCallback = this.protectedByActionCallback;
    const selfTransferActionCallback = this.selfTransferActionCallback;

    const segDeparture = await getStationInfo(s.designator.departureStation);
    const segArrival = await getStationInfo(s.designator.arrivalStation);

    const marketingCarrierCode = s.marketingCarrierCode || s.carrierCode;
    const marketingCarrierName = s.marketingAirlineName || s.airlineName;
    const marketingFlightNumber =
      s.marketingFlightNo ||
      s.marketingFlightNumber ||
      s.flightNo ||
      s.flightNumber;

    let operatingCarrierCode = s.operatingCarrierCode;
    let operatingCarrierName = s.operatingAirlineName;
    let operatingFlightNumber = s.operatingFlightNo || s.operatingFlightNumber;

    if (
      marketingCarrierCode === operatingCarrierCode &&
      marketingFlightNumber === operatingFlightNumber
    ) {
      operatingCarrierCode = '';
      operatingCarrierName = '';
      operatingFlightNumber = '';
    }

    const arrivalTime = dayjs(s.designator.arrivalTimeUtc);
    const departureTime = dayjs(s.designator.departureTimeUtc);
    const departureDate = s.designator.departureTimeUtc.split('T')[0];

    const nextLeg = journeyLegs[i - 1];
    let layoverTime = s.layoverTime;
    let terminalInfo;

    if (!layoverTime && nextLeg) {
      const lastArrivalTime = dayjs(nextLeg.designator.arrivalTimeUtc);
      layoverTime = departureTime.diff(lastArrivalTime) / 1000;
    }

    if (this.terminalInfoFullLoaded) {
      terminalInfo = this.terminalInfoArray.find(
        (t) =>
          t.departureStation.toUpperCase() ===
            s.designator.departureStation.toUpperCase() &&
          t.arrivalStation.toUpperCase() ===
            s.designator.arrivalStation.toUpperCase() &&
          t.carrierCode.toUpperCase() === marketingCarrierCode.toUpperCase() &&
          t.flightNumber.toUpperCase() ===
            marketingFlightNumber.toUpperCase() &&
          t.departureDate === departureDate,
      );
    } else {
      this.loadTeminalInfo(
        s.designator.departureStation,
        s.designator.arrivalStation,
        marketingCarrierCode,
        marketingFlightNumber,
        departureDate,
      );
    }

    const departureTerminal =
      terminalInfo && terminalInfo.departureTerminal
        ? 'T' + terminalInfo.departureTerminal
        : '';
    const arrivalTerminal =
      terminalInfo && terminalInfo.arrivalTerminal
        ? 'T' + terminalInfo.arrivalTerminal
        : '';

    return {
      designator: {
        departureStationCode: s.designator.departureStation,
        departureTime: s.designator.departureTime.replace('.000Z', ''),
        departureAirport: `${s.designator.departureStation} ${segDeparture.airportName} ${departureTerminal}`,
        // departureTerminal,
        departureStation: segDeparture.stationName,
        departureCountryCode: segDeparture.countryCode,
        departureCountry: segDeparture.countryName,
        arrivalStationCode: s.designator.arrivalStation,
        arrivalTime: s.designator.arrivalTime.replace('.000Z', ''),
        arrivalAirport: `${s.designator.arrivalStation} ${segArrival.airportName} ${arrivalTerminal}`,
        // arrivalTerminal,
        arrivalStation: segArrival.stationName,
        arrivalCountryCode: segArrival.countryCode,
        arrivalCountry: segArrival.countryName,
      },
      originalDesignator: s.designator,
      carrier: {
        cabinClass: s.fareClass,
        image: `https://images.kiwi.com/airlines/64/${s.marketingCarrierCode}.png?default=airline.png`,
        marketingCarrierCode,
        marketingCarrierName,
        marketingFlightNumber,
        operatingCarrierCode,
        operatingCarrierName,
        operatingFlightNumber,
      },
      duration: s.duration || arrivalTime.diff(departureTime) / 1000,
      layoverTime,
      isProtected: s.connectionProtectedByAirline,
      isSelfTransfer: s.isSelfTransfer,
      protectedBy: s.connectionProtectedByAirline
        ? t.flights.aaCheckout.journey.carrier
        : '',
      technicalLayover: s.technicalLayover,
      protectedByAction: function noRefCheck() {
        protectedByActionCallback && protectedByActionCallback();
      },
      selfTransferAction: function noRefCheck() {
        selfTransferActionCallback && selfTransferActionCallback();
      },
    };
  }

  async convertJourneyLegDetails(journey) {
    if (journey) {
      const departure = await getStationInfo(
        journey.designator.departureStation,
      );
      const arrival = await getStationInfo(journey.designator.arrivalStation);

      const journeyLegs = [];

      for (const segment of journey.segments) {
        for (const leg of segment.legs) {
          const clonedSegment = JSON.parse(JSON.stringify(segment));
          delete clonedSegment.legs;

          segment.legs.length > 1
            ? journeyLegs.push({
                ...clonedSegment,
                ...leg,
              })
            : journeyLegs.push({
                ...clonedSegment,
              });
        }
      }

      const convertedJourneyLegs = await Promise.all(
        journeyLegs.map(async (s, i) => {
          return await this.convertLeg(journeyLegs, s, i);
        }),
      );

      return {
        totalDaysCount: journey.totalDaysCount,
        designator: {
          departureStationCode: journey.designator.departureStation,
          arrivalStationCode: journey.designator.arrivalStation,
          arrivalTime: journey.designator.arrivalTime.replace('.000Z', ''),
          departureTime: journey.designator.departureTime.replace('.000Z', ''),
          departureAirport: `${departure.airportName}(${journey.designator.departureStation})`,
          departureStation: departure.stationName,
          arrivalAirport: `${arrival.airportName}(${journey.designator.arrivalStation})`,
          arrivalStation: arrival.stationName,
        },
        originalDesignator: journey.designator,
        images: [
          ...new Set(
            journeyLegs.map(
              (s) =>
                `https://images.kiwi.com/airlines/64/${s.marketingCarrierCode}.png?default=airline.png`,
            ),
          ),
        ],
        segments: convertedJourneyLegs,
      };
    }
  }

  async getJourneyLegDetails() {
    const { flightDetails } = this.pageData.flightDetails;

    const journeyDetails = [];

    if (flightDetails.depart) {
      journeyDetails.push(
        await this.convertJourneyLegDetails(flightDetails.depart),
      );
    }

    if (flightDetails.return) {
      journeyDetails.push(
        await this.convertJourneyLegDetails(flightDetails.return),
      );
    }

    return journeyDetails;
  }

  getJourneys(journeyDetails): AACheckout.JourneyLeg[] {
    return journeyDetails.map((j, i) => {
      const { designator, originalDesignator, segments } = j;

      return {
        designator: originalDesignator,
        destination: {
          stationCode: designator.arrivalStationCode,
          stationName: designator.arrivalStation,
        },
        origin: {
          stationCode: designator.departureStationCode,
          stationName: designator.departureStation,
        },
        segments: segments.map((segment) => ({
          arrivalStation: segment.designator.arrivalStationCode,
          arrivalStationName: segment.designator.arrivalStation,
          departureStation: segment.designator.departureStationCode,
          departureStationName: segment.designator.departureStation,
        })),
        tripType: i === 0 ? 'depart' : 'return',
      };
    });
  }

  getStationDetails(journeyDetails: AACheckout.JourneyLegDetail[]) {
    const stationDetails: AACheckout.StationDetails = {};

    journeyDetails.map((journeyDetail) => {
      const { segments } = journeyDetail;
      segments.map((segment) => {
        const { designator } = segment;

        const {
          departureAirport,
          departureCountry,
          departureCountryCode,
          departureStation,
          departureStationCode,
          arrivalAirport,
          arrivalCountry,
          arrivalCountryCode,
          arrivalStation,
          arrivalStationCode,
        } = designator;

        !(departureStationCode in stationDetails) &&
          (stationDetails[departureStationCode] = {
            AirportName: departureAirport,
            StationName: departureStation,
            CountryCode: departureCountryCode,
            CountryName: departureCountry,
          });

        !(arrivalStationCode in stationDetails) &&
          (stationDetails[arrivalStationCode] = {
            AirportName: arrivalAirport,
            StationName: arrivalStation,
            CountryCode: arrivalCountryCode,
            CountryName: arrivalCountry,
          });
      });
    });

    return stationDetails;
  }

  private async loadTeminalInfo(
    departureStation: string,
    arrivalStation: string,
    carrierCode: string,
    flightNumber: string,
    departureDate: string,
  ) {
    let terminalInfo;
    try {
      terminalInfo = await getFlightTerminalInfo(
        departureStation,
        arrivalStation,
        carrierCode,
        flightNumber,
        departureDate,
      );

      terminalInfo && this.terminalInfoArray.push(terminalInfo);

      this.journeyLegDetails = await this.getJourneyLegDetails();

      this.flightDetailsParserCallBack(
        this.journeyLegDetails,
        this.journeyLegs,
        this.stationDetails,
      );
    } catch (error) {
      // ignore error
      console.log('terminal info load error', error);
    }
  }

  async parse() {
    this.journeyLegDetails = await this.getJourneyLegDetails();
    this.journeyLegs = this.getJourneys(this.journeyLegDetails);
    this.stationDetails = this.getStationDetails(this.journeyLegDetails);
    this.terminalInfoFullLoaded = true;

    this.flightDetailsParserCallBack(
      this.journeyLegDetails,
      this.journeyLegs,
      this.stationDetails,
    );
  }
}
